import React from 'react'
import Turnstile from 'react-turnstile'
import { Input } from '@quest-lock/ui/input'
import { Label } from '@quest-lock/ui/label'
import { Textarea } from '@quest-lock/ui/textarea'
import { Checkbox } from '@quest-lock/ui/checkbox'
import { StarRating } from '@/components/contact/StarRating'
import { FormData, FormErrors } from '@/types/contact'

interface ContactFormFieldsProps {
  formData: FormData
  errors: FormErrors
  rating: number
  isConsentChecked: boolean
  handleInputChange: (field: keyof FormData, value: string) => void
  setRating: (value: number) => void
  setIsConsentChecked: (checked: boolean) => void
  handleTurnstileChange: (
    token: string | null,
    error?: 'error' | 'expired'
  ) => void
}

const getTextAreaPlaceholder = (category: string): string => {
  switch (category) {
    case 'idea':
      return 'What new feature would you like to see?'
    case 'testimonial':
      return 'How has Quest-Lock helped you?'
    case 'error':
      return 'Please describe the bug or issue you encountered.'
    default:
      return 'What would you like to tell us?'
  }
}

export const ContactFormFields: React.FC<ContactFormFieldsProps> = ({
  formData,
  errors,
  rating,
  isConsentChecked,
  handleInputChange,
  setRating,
  setIsConsentChecked,
  handleTurnstileChange
}) => {
  const isTestimonial = formData.category === 'testimonial'

  return (
    <div className="space-y-3 sm:space-y-6">
      {isTestimonial && (
        <>
          <div className="space-y-2">
            <h5 className="font-heading text-foreground mb-2 text-lg">
              Share Your Story
            </h5>
            <div className="font-body text-muted-foreground space-y-1 text-sm">
              <p>• How did Quest-Lock help you achieve your goals?</p>
              <p>• What advice would you give to someone starting out?</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="font-body">Your Rating</Label>
            <StarRating value={rating} onChange={setRating} size={32} />
          </div>
        </>
      )}

      <div className="space-y-2">
        <Label htmlFor="message" className="font-body">
          Message
        </Label>
        <Textarea
          id="message"
          placeholder={getTextAreaPlaceholder(formData.category)}
          value={formData.message}
          onChange={e => handleInputChange('message', e.target.value)}
          className={`font-body ${errors.message ? 'border-destructive' : ''}`}
          rows={3}
        />
        {errors.message && (
          <p className="font-body text-destructive mt-1 text-xs">
            {errors.message}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name" className="font-body">
            Name
          </Label>
          <Input
            id="name"
            placeholder="Your Name"
            value={formData.name}
            onChange={e => handleInputChange('name', e.target.value)}
            className={`font-body ${errors.name ? 'border-destructive' : ''}`}
          />
          {errors.name && (
            <p className="font-body text-destructive mt-1 text-xs">
              {errors.name}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="font-body">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="your.email@example.com"
            value={formData.email}
            onChange={e => handleInputChange('email', e.target.value)}
            className={`font-body ${errors.email ? 'border-destructive' : ''}`}
          />
          {errors.email && (
            <p className="font-body text-destructive mt-1 text-xs">
              {errors.email}
            </p>
          )}
        </div>
      </div>

      {isTestimonial && (
        <div className="flex space-x-2 pt-1 sm:space-x-3 sm:pt-2">
          <Checkbox
            id="terms1"
            checked={isConsentChecked}
            onCheckedChange={checked => setIsConsentChecked(Boolean(checked))}
            className="mt-0.5"
          />
          <div className="grid gap-1.5 leading-none">
            <label
              htmlFor="terms1"
              className="font-body text-sm leading-tight peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              I grant permission for this testimonial to be used in marketing
              materials.
            </label>
          </div>
        </div>
      )}

      <div className="space-y-1 pt-1 sm:space-y-2 sm:pt-2">
        <Label className="font-body">Security Verification</Label>
        <div className="bg-background flex w-full justify-center rounded-md p-2">
          <Turnstile
            sitekey={
              import.meta.env.VITE_CLOUDFLARE_SITEKEY ||
              '1x00000000000000000000AA'
            }
            onVerify={token => handleTurnstileChange(token)}
            onError={() => handleTurnstileChange(null, 'error')}
            onExpire={() => handleTurnstileChange(null, 'expired')}
          />
        </div>
        {errors.turnstile && (
          <p className="font-body text-destructive mt-1 text-center text-xs">
            {errors.turnstile}
          </p>
        )}
      </div>
    </div>
  )
}
